import { ScrollView, StyleSheet, View } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import type ApiBathCardsResponse from 'src/core/models/api/responses/ApiBathCardsResponse';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useBathCardsStore } from 'src/domain/stores/useBathCardsStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useWorkspacesStore } from 'src/domain/stores/useWorkspacesStore';
import { BathCardVariant } from 'src/domain/models/BathCardVariant';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import type { PickerItem } from 'src/presentation/components/molecules/SelectField';
import BathCardLinkOrBuyCard from 'src/presentation/components/organisms/BathCardLinkOrBuyCard';
import BathCardUspsCard from 'src/presentation/components/organisms/BathCardUspsCard';
import PromotionCard from 'src/presentation/components/organisms/PromotionCard';
import BathCardsSlider from 'src/presentation/components/organisms/BathCardsSlider';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import AddBathCardDialog from 'src/presentation/dialogs/AddBathCardDialog';
import RemoveBathCardDialog from 'src/presentation/dialogs/RemoveBathCardDialog';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import useBathCardActions from 'src/presentation/hooks/useBathCardActions';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';

export default function BathCardsScreen() {
	const { user } = useAuthStore();
	const { workspaces, workspacesLoading, workspacesErrorMessage, fetchWorkspaces } = useWorkspacesStore();
	const {
		bathCards,
		usps,
		promotion,
		cardsPage,
		isLoading,
		isAddingCard,
		hasError,
		addCardErrorMessage,
		addCardBarcode,
		selectedWorkspaceId,
		isAddCardDialogVisible,
		init,
		getApiBathCards,
		postApiBathCard,
		openAddCardDialog,
		closeAddCardDialog,
		openRemoveCardDialog,
		setAddCardBarcode,
		setSelectedWorkspaceId,
		setAddCardErrorMessage,
		setCardsPage,
	} = useBathCardsStore();
	const { logEvent } = useServicesStore();
	const { openBathCardPurchase, openReservationWithBathCard } = useBathCardActions();
	const tabBarInset = useTabBarInset();

	const cards = bathCards ?? [];

	const fetchData = async () => {
		const requests = [getApiBathCards()];

		if (workspaces.length === 0) {
			requests.push(fetchWorkspaces());
		}

		await Promise.all(requests);
	};

	useAppStateScreenListener({
		onInit: () => init(),
		onFocussed: () => fetchData(),
		onForegrounded: () => fetchData(),
	});

	const doAddCard = async () => {
		let workspaceId: string | null = null;
		if (selectedWorkspaceId !== Constants.workspacesDefaultItemValue) {
			workspaceId = getWorkspaceItems().find((w) => w.value === selectedWorkspaceId)?.value ?? null;
		}

		if (!workspaceId) {
			setAddCardErrorMessage(Translations.selectAThermenResort);
			return;
		}

		await postApiBathCard({
			workspaceId,
			barcode: addCardBarcode,
			onSuccess: async (response) => {
				const responseData: ApiBathCardsResponse | null = response.data;
				const newlyAdded = responseData?.data.find((card) => card.barcode === addCardBarcode);

				if (newlyAdded) {
					logEvent({
						name: Constants.analyticsEventAddBathcard,
						params: {
							[Constants.analyticsParamScreenPage]: Constants.screenBathCardsList,
							[Constants.analyticsParamSelectItem]: newlyAdded.name,
						},
						user,
					});
				}

				closeAddCardDialog();
				await getApiBathCards();
			},
		});
	};

	const getWorkspaceItems: () => Array<PickerItem> = () => {
		const mappedWorkspaces = workspaces.map((w) => {
			return { label: w.title, value: w.id.toString() };
		});

		const defaultItem = {
			label: Translations.selectAThermenResort,
			value: Constants.workspacesDefaultItemValue,
		};
		return [defaultItem, ...mappedWorkspaces];
	};

	const linkOrBuyCard = (
		<BathCardLinkOrBuyCard
			heading={cards.length === 0 ? Translations.bathcardNoCardHeading : Translations.bathcardExtraCardHeading}
			style={styles.blockInset}
			onPressBuy={() => openBathCardPurchase()}
			onPressLink={() => openAddCardDialog()}
		/>
	);

	return (
		<View style={styles.container}>
			<StatusBar style={'dark'} />
			<BackgroundPattern />
			<RemoveBathCardDialog />

			<AddBathCardDialog
				barcodeText={addCardBarcode}
				errorBarcode={addCardErrorMessage}
				isLoading={isAddingCard}
				items={getWorkspaceItems()}
				selectedValue={selectedWorkspaceId}
				setBarcodeText={setAddCardBarcode}
				setSelectedValue={setSelectedWorkspaceId}
				title={Translations.bathcardAddBathcard}
				visible={isAddCardDialogVisible}
				onPress={() => closeAddCardDialog()}
				onPressClaim={() => doAddCard()}
			/>

			{isLoading || workspacesLoading ? (
				<LoadingIndicator style={styles.flex1} />
			) : workspacesErrorMessage || (hasError && bathCards === null) ? (
				<ErrorView onPress={fetchData} />
			) : (
				<ScrollView contentContainerStyle={[styles.content, { paddingBottom: tabBarInset + Dimens.padding24 }]}>
					{cards.length === 0 ? (
						<>
							{linkOrBuyCard}
							<PromotionCard promotion={promotion} style={styles.blockInset} />
							<View style={styles.block}>
								<BathCardUspsCard usps={usps} />
							</View>
						</>
					) : (
						<>
							<BathCardsSlider
								cards={cards}
								page={cardsPage}
								setPage={setCardsPage}
								variant={BathCardVariant.Barcode}
								onPressRemove={(card) => openRemoveCardDialog(card)}
								onPressReserve={(card) => openReservationWithBathCard(card)}
							/>
							<PromotionCard promotion={promotion} style={styles.blockInset} />
							{linkOrBuyCard}
						</>
					)}
				</ScrollView>
			)}
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: Dimens.flex1,
	},
	flex1: {
		flex: Dimens.flex1,
	},
	content: {
		paddingTop: Dimens.padding24,
		gap: Dimens.gap16,
	},
	block: {
		paddingHorizontal: Dimens.padding18,
	},
	blockInset: {
		marginHorizontal: Dimens.padding18,
	},
});
